import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import QRCode from "qrcode";

export type ErrorCorrectionLevel = "L" | "M" | "Q" | "H";

export interface QRCodeGeneratorProps {
  data: string;
  size?: number;
  logoUrl?: string;
  showLogo?: boolean;
  fgColor?: string;
  bgColor?: string;
  ecLevel?: ErrorCorrectionLevel;
  className?: string;
  onReady?: () => void;
  onError?: (message: string) => void;
  ariaLabel?: string;
}

export type QRImageExtension = "png" | "jpeg" | "webp";

export interface GetBlobOptions {
  ext?: QRImageExtension;
  exportSize?: number;
}

export interface QRCodeGeneratorHandle {
  getBlob: (
    extOrOptions?: QRImageExtension | GetBlobOptions
  ) => Promise<Blob | null>;
}

const DEFAULT_LOGO_FALLBACK = "/images/logo.png";

const QRCodeGenerator = forwardRef<QRCodeGeneratorHandle, QRCodeGeneratorProps>(
  (
    {
      data,
      size = 256,
      logoUrl = DEFAULT_LOGO_FALLBACK,
      showLogo = true,
      fgColor = "#000000",
      bgColor = "#FFFFFF",
      ecLevel = "H",
      className = "",
      onReady,
      onError,
      ariaLabel,
    },
    ref
  ) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const generateQR = useCallback(async () => {
      if (!canvasRef.current || !data) return;

      setIsLoading(true);
      setError(null);

      try {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          throw new Error("Could not get canvas context");
        }

        canvas.width = size;
        canvas.height = size;

        const qrOptions = {
          errorCorrectionLevel: ecLevel,
          type: "image/png" as const,
          quality: 1,
          margin: 1,
          color: {
            dark: fgColor,
            light: bgColor,
          },
          width: size,
        };

        await QRCode.toCanvas(canvas, data, qrOptions);

        if (showLogo && logoUrl) {
          try {
            await addLogoToCanvas(canvas, logoUrl, size);
          } catch (logoError) {
            console.warn("Failed to add logo:", logoError);
          }
        }

        onReady?.();
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Failed to generate QR code";
        setError(msg);
        onError?.(msg);
      } finally {
        setIsLoading(false);
      }
    }, [
      data,
      size,
      logoUrl,
      showLogo,
      fgColor,
      bgColor,
      ecLevel,
      onReady,
      onError,
    ]);

    const addLogoToCanvas = async (
      canvas: HTMLCanvasElement,
      logoUrl: string,
      canvasSize: number
    ) => {
      return new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";

        img.onload = () => {
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject(new Error("No canvas context"));
            return;
          }

          const logoSize = Math.floor(canvasSize * 0.2);
          const logoX = (canvasSize - logoSize) / 2;
          const logoY = (canvasSize - logoSize) / 2;
          const padding = Math.max(4, Math.round(canvasSize * 0.016));
          const borderRadius = Math.max(8, Math.round(canvasSize * 0.032));

          ctx.fillStyle = bgColor;
          ctx.beginPath();
          ctx.roundRect(
            logoX - padding,
            logoY - padding,
            logoSize + padding * 2,
            logoSize + padding * 2,
            borderRadius + 2
          );
          ctx.fill();

          ctx.save();
          ctx.beginPath();
          ctx.roundRect(logoX, logoY, logoSize, logoSize, borderRadius);
          ctx.closePath();
          ctx.clip();
          ctx.drawImage(img, logoX, logoY, logoSize, logoSize);
          ctx.restore();
          resolve();
        };

        img.onerror = () => reject(new Error("Failed to load logo"));
        img.src = logoUrl;
      });
    };

    useEffect(() => {
      generateQR();
    }, [generateQR]);

    const getBlob = useCallback(
      async (
        extOrOptions: QRImageExtension | GetBlobOptions = "png"
      ): Promise<Blob | null> => {
        const options: GetBlobOptions =
          typeof extOrOptions === "string"
            ? { ext: extOrOptions }
            : extOrOptions;
        const ext: QRImageExtension = options.ext ?? "png";
        const exportSize = options.exportSize;

        const exportFromCanvas = (canvas: HTMLCanvasElement) =>
          new Promise<Blob | null>((resolve) => {
            canvas.toBlob((blob) => resolve(blob), `image/${ext}`, 1.0);
          });

        if (!exportSize || exportSize <= size) {
          if (!canvasRef.current) return null;
          return exportFromCanvas(canvasRef.current);
        }

        const offscreen = document.createElement("canvas");
        offscreen.width = exportSize;
        offscreen.height = exportSize;

        await QRCode.toCanvas(offscreen, data, {
          errorCorrectionLevel: ecLevel,
          margin: 1,
          color: { dark: fgColor, light: bgColor },
          width: exportSize,
        });

        if (showLogo && logoUrl) {
          try {
            await addLogoToCanvas(offscreen, logoUrl, exportSize);
          } catch (logoError) {
            console.warn("Failed to add logo to high-res export:", logoError);
          }
        }

        return exportFromCanvas(offscreen);
      },
      [data, size, ecLevel, fgColor, bgColor, showLogo, logoUrl]
    );

    useImperativeHandle(ref, () => ({ getBlob }), [getBlob]);

    return (
      <div className={`flex justify-center ${className}`}>
        <div
          role="img"
          aria-label={ariaLabel || "QR code"}
          style={{
            maxWidth: size,
            maxHeight: size,
            width: "100%",
            aspectRatio: "1/1",
          }}
          className={[
            "transition-all duration-200 relative",
            isLoading ? "opacity-50" : "",
          ].join(" ")}
          data-has-error={Boolean(error)}
        >
          <canvas
            ref={canvasRef}
            style={{
              width: "100%",
              height: "100%",
              maxWidth: "100%",
              objectFit: "contain",
              display: error ? "none" : "block",
            }}
            className="rounded"
          />

          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-lg">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
            </div>
          )}

          {!!error && !isLoading && (
            <div className="absolute inset-0 flex items-center justify-center p-2">
              <div className="text-red-500 text-xs text-center">
                <div className="mb-1">⚠️</div>
                <div>{error}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

QRCodeGenerator.displayName = "QRCodeGenerator";

export default QRCodeGenerator;
